import { useEffect, useState } from "react";

import { getVenueXPreviewPayload } from "@/data/previewContent";
import { localizedApiHref } from "@/i18n/locale";
import { localizeContent, uiText } from "@/i18n/translations";

export type DynamicPageKind = "service" | "portfolio" | "venue" | "menu";

export type DynamicLink = { label: string; href: string };

export type DynamicArchiveData = {
  kind: "venue" | "menu";
  title: string;
  intro: string;
  seoTitle: string;
  searchDescription: string;
  presentation: {
    eyebrow: string;
    hero_title: string;
    intro: string;
    empty_message: string;
    detail_eyebrow: string;
    back_label: string;
    primary_link: DynamicLink;
    secondary_link: DynamicLink;
  };
  items: Array<{
    id: number;
    slug: string;
    url: string;
    title: string;
    summary: string;
    imageUrl: string;
    imageAlt: string;
    imagePosition: string;
    meta: string[];
  }>;
};

export type DynamicDetailData = {
  kind: DynamicPageKind;
  title: string;
  slug: string;
  seoTitle: string;
  searchDescription: string;
  eyebrow: string;
  summary: string;
  bodyHtml: string;
  imageUrl: string;
  imageAlt: string;
  imagePosition: string;
  meta: string[];
  links: {
    back: DynamicLink;
    primary: DynamicLink;
    secondary: DynamicLink;
  };
};

type RawArchive = Omit<DynamicArchiveData, "seoTitle" | "searchDescription" | "items"> & {
  seo_title: string;
  search_description: string;
  items: Array<{
    id: number;
    slug: string;
    url: string;
    title: string;
    summary: string;
    image_url: string;
    image_alt: string;
    image_position: string;
    meta: string[];
  }>;
};

type RawDetail = Omit<DynamicDetailData, "seoTitle" | "searchDescription" | "bodyHtml" | "imageUrl" | "imageAlt" | "imagePosition"> & {
  seo_title: string;
  search_description: string;
  body_html: string;
  image_url: string;
  image_alt: string;
  image_position: string;
};

const endpointNames: Record<DynamicPageKind, string> = {
  service: "services",
  portfolio: "portfolio",
  venue: "venues",
  menu: "menus",
};

function mapArchive(response: RawArchive): DynamicArchiveData {
  return localizeContent({
    kind: response.kind,
    title: response.title,
    intro: response.intro,
    seoTitle: response.seo_title,
    searchDescription: response.search_description,
    presentation: response.presentation,
    items: response.items.map((item) => ({
      id: item.id,
      slug: item.slug,
      url: item.url,
      title: item.title,
      summary: item.summary,
      imageUrl: item.image_url,
      imageAlt: item.image_alt,
      imagePosition: item.image_position,
      meta: item.meta,
    })),
  });
}

function mapDetail(response: RawDetail): DynamicDetailData {
  return localizeContent({
    kind: response.kind,
    title: response.title,
    slug: response.slug,
    seoTitle: response.seo_title,
    searchDescription: response.search_description,
    eyebrow: response.eyebrow,
    summary: response.summary,
    bodyHtml: response.body_html,
    imageUrl: response.image_url,
    imageAlt: response.image_alt,
    imagePosition: response.image_position,
    meta: response.meta,
    links: response.links,
  });
}

export function useDynamicArchive(kind: "venue" | "menu") {
  const previewContent = getVenueXPreviewPayload<RawArchive>("dynamic-archive");
  const matchingPreview = previewContent?.kind === kind ? previewContent : null;
  const [data, setData] = useState<DynamicArchiveData | null>(() =>
    matchingPreview ? mapArchive(matchingPreview) : null,
  );
  const [error, setError] = useState("");

  useEffect(() => {
    if (matchingPreview) return;

    const controller = new AbortController();
    fetch(localizedApiHref(`/api/${endpointNames[kind]}/`), {
      signal: controller.signal,
      headers: { Accept: "application/json" },
    })
      .then((response) => {
        if (!response.ok) throw new Error(uiText("This archive is not available yet."));
        return response.json() as Promise<RawArchive>;
      })
      .then((response) => setData(mapArchive(response)))
      .catch((reason: unknown) => {
        if (!(reason instanceof DOMException && reason.name === "AbortError")) {
          setError(reason instanceof Error ? reason.message : uiText("This archive is not available yet."));
        }
      });
    return () => controller.abort();
  }, [kind, matchingPreview]);

  return { data, error };
}

export function useDynamicDetail(kind: DynamicPageKind, slug: string) {
  const previewContent = getVenueXPreviewPayload<RawDetail>("dynamic-detail");
  const matchingPreview = previewContent?.kind === kind ? previewContent : null;
  const [data, setData] = useState<DynamicDetailData | null>(() =>
    matchingPreview ? mapDetail(matchingPreview) : null,
  );
  const [error, setError] = useState("");

  useEffect(() => {
    if (matchingPreview) return;

    const controller = new AbortController();
    setData(null);
    setError("");
    fetch(localizedApiHref(`/api/${endpointNames[kind]}/${encodeURIComponent(slug)}/`), {
      signal: controller.signal,
      headers: { Accept: "application/json" },
    })
      .then((response) => {
        if (!response.ok) throw new Error(uiText("This page has not been published yet."));
        return response.json() as Promise<RawDetail>;
      })
      .then((response) => setData(mapDetail(response)))
      .catch((reason: unknown) => {
        if (!(reason instanceof DOMException && reason.name === "AbortError")) {
          setError(reason instanceof Error ? reason.message : uiText("This page has not been published yet."));
        }
      });
    return () => controller.abort();
  }, [kind, matchingPreview, slug]);

  return { data, error };
}
